import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/Dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  Truck, 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  FileText,
  X,
  Plus,
  MapPin,
  RefreshCw,
  Info,
  FileSpreadsheet,
  Eye,
  Trash2,
  Activity
} from 'lucide-react';
import { apiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { 
  validateAmbulanceCsvStructure, 
  validateAmbulanceData, 
  parseCsvFile, 
  generateAmbulanceCsvTemplate, 
  downloadCsvFile,
  checkDuplicatePlateNumbers
} from '../../utils/csvValidation';

const BulkAmbulanceRegistrationModal = ({ onSuccess, onError, children, inline = false, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationMode, setRegistrationMode] = useState('manual');
  const [ambulances, setAmbulances] = useState([]);
  const [errors, setErrors] = useState({});
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const { user } = useAuth();

  // Load ambulance types on component mount
  useEffect(() => {
    const loadAmbulanceTypes = async () => {
      try {
        const response = await apiService.getAmbulanceTypes();
        let types = [];
        if (response.success && response.data) {
          types = response.data;
        } else if (Array.isArray(response)) {
          types = response;
        }
        setAmbulanceTypes(types);
      } catch (error) {
        console.error('Error loading ambulance types:', error);
        setAmbulanceTypes([
          { type_id: 'fallback-1', name: 'Basic Life Support (BLS)' },
          { type_id: 'fallback-2', name: 'Advanced Life Support (ALS)' },
          { type_id: 'fallback-3', name: 'Critical Care' }
        ]);
      }
    };
    
    if (isOpen) {
      loadAmbulanceTypes();
    }
  }, [isOpen]);

  const addAmbulance = () => {
    setAmbulances(prev => [...prev, {
      plate_number: '',
      type_id: '',
      current_latitude: '',
      current_longitude: '',
      status: 'AVAILABLE'
    }]);
  };

  const removeAmbulance = (index) => {
    setAmbulances(prev => prev.filter((_, i) => i !== index));
  };

  const updateAmbulance = useCallback((index, field, value) => {
    setAmbulances(prev => prev.map((ambulance, i) => 
      i === index ? { ...ambulance, [field]: value } : ambulance
    ));
  }, []);

  const validateAmbulances = () => {
    const newErrors = {};
    
    if (ambulances.length === 0) {
      newErrors.general = 'Please add at least one ambulance';
      setErrors(newErrors);
      return false;
    }
    
    ambulances.forEach((ambulance, index) => {
      if (!ambulance.plate_number.trim()) {
        newErrors[`ambulance_${index}_plate_number`] = 'Plate number is required';
      } else if (ambulance.plate_number.trim().length < 5) {
        newErrors[`ambulance_${index}_plate_number`] = 'Plate number must be at least 5 characters';
      }
      
      if (!ambulance.type_id) {
        newErrors[`ambulance_${index}_type_id`] = 'Ambulance type is required';
      }
      
      if (ambulance.current_latitude && (isNaN(ambulance.current_latitude) || ambulance.current_latitude < -90 || ambulance.current_latitude > 90)) {
        newErrors[`ambulance_${index}_current_latitude`] = 'Please enter a valid latitude (-90 to 90)';
      }
      
      if (ambulance.current_longitude && (isNaN(ambulance.current_longitude) || ambulance.current_longitude < -180 || ambulance.current_longitude > 180)) {
        newErrors[`ambulance_${index}_current_longitude`] = 'Please enter a valid longitude (-180 to 180)';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Starting bulk ambulance registration...');
    console.log('📊 Ambulances to register:', ambulances.length);
    
    if (!validateAmbulances()) {
      console.log('❌ Validation failed');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const operatorId = user?.operator?.operator_id || 
                        user?.operator_id || 
                        user?.operator?.id ||
                        user?.fleet_operator_id ||
                        user?.fleet_operator?.operator_id;
      
      console.log('👤 User object:', user);
      console.log('👤 Operator ID:', operatorId);
      
      if (!operatorId) {
        throw new Error('Operator ID not found. Please ensure you are logged in as a fleet operator.');
      }

      const ambulancesData = ambulances.map(ambulance => ({
        plate_number: ambulance.plate_number.trim().toUpperCase(),
        type_id: ambulance.type_id,
        operator_id: operatorId,
        current_latitude: ambulance.current_latitude ? parseFloat(ambulance.current_latitude) : null,
        current_longitude: ambulance.current_longitude ? parseFloat(ambulance.current_longitude) : null,
        status: ambulance.status ? ambulance.status.toUpperCase() : 'AVAILABLE'
      }));
      
      console.log('📤 Sending data to API:', { ambulances: ambulancesData, operator_id: operatorId });
      
      const result = await apiService.bulkRegisterAmbulances({ 
        ambulances: ambulancesData,
        operator_id: operatorId
      });
      
      console.log('✅ API response:', result);
      
      if (result.success) {
        const { successful, errors, summary } = result.data;
        
        console.log('📊 Registration summary:', summary);
        console.log('✅ Successful registrations:', successful);
        console.log('❌ Failed registrations:', errors);
        
        if (errors && errors.length > 0) {
          // Handle partial success/failure
          const duplicateErrors = errors.filter(err => err.error.includes('Unique constraint failed'));
          const otherErrors = errors.filter(err => !err.error.includes('Unique constraint failed'));
          
          let errorMessage = '';
          
          if (duplicateErrors.length > 0) {
            const duplicatePlates = duplicateErrors.map(err => err.plate_number).join(', ');
            errorMessage += `${duplicateErrors.length} ambulances already exist (${duplicatePlates}). `;
          }
          
          if (otherErrors.length > 0) {
            errorMessage += `${otherErrors.length} ambulances failed validation. `;
          }
          
          if (successful && successful.length > 0) {
            errorMessage += `${successful.length} ambulances registered successfully. `;
          }
          
          errorMessage += 'Please check the console for details.';
          
          // Show detailed error information
          console.error('🚨 Detailed registration errors:', errors);
          onError && onError(errorMessage);
        } else {
          // All successful
          onSuccess && onSuccess(result.data);
          setIsOpen(false);
          setAmbulances([]);
          setErrors({});
        }
      }
    } catch (error) {
      console.error('Bulk registration error:', error);
      
      let errorMessage = 'Failed to register ambulances';
      if (error.message?.includes('already assigned')) {
        errorMessage = 'One or more drivers are already assigned to other ambulances. Please check your data.';
      } else if (error.message?.includes('already exists')) {
        errorMessage = 'One or more ambulances with this information already exist. Please check your data.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      onError && onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (inline && onCancel) {
      onCancel();
    } else {
      setIsOpen(false);
    }
    setAmbulances([]);
    setErrors({});
    setRegistrationMode('manual');
    clearCsvData();
  };

  const downloadTemplate = () => {
    const csvContent = generateAmbulanceCsvTemplate(ambulanceTypes);
    downloadCsvFile(csvContent, 'ambulance_bulk_template.csv');
  };

  const handleFileUpload = (event) => {
    console.log('🔄 CSV file upload started');
    const file = event.target.files[0];
    if (!file) {
      console.log('❌ No file selected');
      return;
    }

    console.log('📁 Selected file:', file.name, 'Type:', file.type, 'Size:', file.size);

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      console.log('❌ Invalid file type:', file.type);
      onError && onError('Please select a valid CSV file');
      return;
    }

    setCsvFile(file);
    setUploadProgress(0);
    setCsvValidationErrors([]);

    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        setUploadProgress((e.loaded / e.total) * 100);
      }
    };

    reader.onload = (e) => {
      try {
        console.log('📖 File read completed, parsing CSV...');
        const csv = e.target.result;
        console.log('📄 CSV content length:', csv.length);
        
        const { headers, data } = parseCsvFile(csv);
        console.log('📋 Parsed headers:', headers);
        console.log('📊 Parsed data rows:', data.length);
        
        const structureValidation = validateAmbulanceCsvStructure(headers);
        console.log('🔍 Structure validation result:', structureValidation);
        
        if (!structureValidation.isValid) {
          console.log('❌ Structure validation failed:', structureValidation.errors);
          setCsvValidationErrors(structureValidation.errors);
          onError && onError('CSV structure validation failed. Please check the file format.');
          return;
        }
        
        const validationErrors = [];
        data.forEach((row, index) => {
          const rowErrors = validateAmbulanceData(row, index);
          validationErrors.push(...rowErrors);
        });

        // Check for duplicate plate numbers within the CSV
        const duplicateErrors = checkDuplicatePlateNumbers(data);
        validationErrors.push(...duplicateErrors);

        console.log('🔍 Data validation errors:', validationErrors.length);
        console.log('🔍 Duplicate plate number errors:', duplicateErrors.length);

        if (validationErrors.length > 0) {
          console.log('❌ Data validation failed:', validationErrors);
          setCsvValidationErrors(validationErrors);
          onError && onError('CSV data validation failed. Please check the data.');
          return;
        }

        console.log('✅ CSV validation passed, setting data...');
        setCsvData(data);
        setPreviewMode(true);
        setUploadProgress(100);
      } catch (error) {
        console.error('❌ CSV processing error:', error);
        onError && onError(error.message);
      }
    };

    reader.readAsText(file);
  };

  const processCsvData = () => {
    console.log('🔄 Processing CSV data with type ID conversion...');
    console.log('📋 Available ambulance types:', ambulanceTypes);
    
    const processedAmbulances = csvData.map((row, index) => {
      // Convert simple number (1-7) to actual type_id
      let actualTypeId = row.type_id;
      
      if (row.type_id && !isNaN(row.type_id)) {
        const typeIndex = parseInt(row.type_id) - 1; // Convert 1-based to 0-based index
        if (typeIndex >= 0 && typeIndex < ambulanceTypes.length) {
          actualTypeId = ambulanceTypes[typeIndex].type_id;
          console.log(`🔄 Row ${index + 1}: Converted type_id ${row.type_id} → ${actualTypeId} (${ambulanceTypes[typeIndex].name})`);
        } else {
          console.warn(`⚠️ Row ${index + 1}: Invalid type_id ${row.type_id}, using as-is`);
        }
      }
      
      return {
        plate_number: row.plate_number || '',
        type_id: actualTypeId || '',
        current_latitude: row.current_latitude || '',
        current_longitude: row.current_longitude || '',
        status: row.status ? row.status.toUpperCase() : 'AVAILABLE'
      };
    });

    console.log('✅ Processed ambulances:', processedAmbulances);
    setAmbulances(processedAmbulances);
    setPreviewMode(false);
    setRegistrationMode('manual');
  };

  const clearCsvData = () => {
    setCsvData([]);
    setCsvFile(null);
    setPreviewMode(false);
    setUploadProgress(0);
    setCsvValidationErrors([]);
  };

  const csvUploadSection = (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            CSV Upload
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download Template</span>
          </Button>
        </CardTitle>
        <CardDescription>
          Upload a CSV file with ambulance data. Download the template to see the required format.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* CSV Structure Information */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Required CSV Structure
          </h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p className="font-medium">Your CSV file must contain these exact columns:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {['plate_number', 'type_id', 'current_latitude', 'current_longitude', 'status'].map((header) => (
                <div key={header} className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-xs">{header}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ambulance Types Reference */}
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Ambulance Types Reference
          </h4>
          <div className="text-sm text-yellow-800">
            <p className="mb-2">Use these simple numbers in your CSV (our system will map them to actual type IDs):</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ambulanceTypes.map((type, index) => (
                <div 
                  key={type.type_id} 
                  className="flex items-center justify-between bg-white p-2 rounded border cursor-help"
                  title={type.name}
                >
                  {/* Show only abbreviation */}
                  <span className="font-semibold text-gray-900">{type.abbreviation || type.name}</span>
                  {/* Full name commented out - hover shows full name: */}
                  {/* <span className="text-xs text-gray-400">({type.name})</span> */}
                  <span className="text-xs bg-yellow-100 px-2 py-1 rounded">Use: {index + 1}</span>
                </div>
              ))}
            </div>
            {ambulanceTypes.length === 0 && (
              <p className="text-yellow-700 italic">Loading ambulance types...</p>
            )}
          </div>
        </div>

        {!previewMode ? (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Drag and drop your CSV file here, or click to browse</p>
              <p className="text-sm text-gray-500 mb-4">Supported format: CSV files only</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('csv-upload').click()}
              >
                Choose File
              </Button>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {csvFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">{csvFile.name}</p>
                      <p className="text-sm text-blue-700">
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearCsvData}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {csvValidationErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h4 className="font-medium text-red-900">Validation Errors</h4>
                </div>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm text-red-800 space-y-1">
                    {csvValidationErrors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-600 mt-0.5">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">CSV Preview</h4>
                  <p className="text-xs text-gray-500">Simple numbers (1-7) will be converted to actual type IDs</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{csvData.length} ambulances</Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearCsvData}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Duplicate Warning */}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">⚠️ Duplicate Plate Numbers Warning</p>
                    <p>If any plate numbers already exist in the database, those registrations will fail. Check your data carefully before proceeding.</p>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3">Plate Number</th>
                      <th className="text-left py-2 px-3">Type ID (Converted)</th>
                      <th className="text-left py-2 px-3">Latitude</th>
                      <th className="text-left py-2 px-3">Longitude</th>
                      <th className="text-left py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.slice(0, 5).map((row, index) => {
                      // Show converted type ID in preview
                      let displayTypeId = row.type_id;
                      let typeName = '';
                      
                      if (row.type_id && !isNaN(row.type_id)) {
                        const typeIndex = parseInt(row.type_id) - 1;
                        if (typeIndex >= 0 && typeIndex < ambulanceTypes.length) {
                          displayTypeId = ambulanceTypes[typeIndex].type_id;
                          typeName = ambulanceTypes[typeIndex].name;
                        }
                      }
                      
                      return (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-2 px-3">{row.plate_number}</td>
                          <td className="py-2 px-3">
                            <div className="flex flex-col">
                              <span className="text-xs text-gray-500">{row.type_id} →</span>
                              <span className="font-medium">{displayTypeId}</span>
                              {typeName && <span className="text-xs text-blue-600">{typeName}</span>}
                            </div>
                          </td>
                          <td className="py-2 px-3">{row.current_latitude}</td>
                          <td className="py-2 px-3">{row.current_longitude}</td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-xs">
                              {row.status}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {csvData.length > 5 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Showing first 5 rows of {csvData.length} total rows
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={clearCsvData}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={processCsvData}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Process Data
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const manualEntrySection = (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Ambulances to Register</h3>
        <Button
          type="button"
          onClick={addAmbulance}
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Ambulance</span>
        </Button>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-800">{errors.general}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {ambulances.map((ambulance, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base">Ambulance {index + 1}</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeAmbulance(index)}
                className="text-red-600 hover:text-red-700 h-9 w-9 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Plate Number *
                  </label>
                  <Input
                    placeholder="GJ-01-AB-1234"
                    value={ambulance.plate_number}
                    onChange={(e) => updateAmbulance(index, 'plate_number', e.target.value)}
                    className={`text-base ${errors[`ambulance_${index}_plate_number`] ? 'border-red-500' : ''}`}
                  />
                  {errors[`ambulance_${index}_plate_number`] && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      {errors[`ambulance_${index}_plate_number`]}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Ambulance Type *
                  </label>
                  <Select 
                    value={ambulance.type_id} 
                    onValueChange={(value) => updateAmbulance(index, 'type_id', value)}
                  >
                    <SelectTrigger className={`text-base ${errors[`ambulance_${index}_type_id`] ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select ambulance type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ambulanceTypes.map((type) => (
                        <SelectItem key={type.type_id} value={type.type_id}>
                          <div 
                            className="flex items-center justify-between w-full cursor-help"
                            title={type.name}
                          >
                            {/* Show only abbreviation */}
                            <span className="text-sm font-semibold text-gray-900">
                              {type.abbreviation || type.name}
                            </span>
                            {/* Full name commented out for now - hover shows full name: */}
                            {/* <span className="text-xs text-gray-400">({type.name})</span> */}
                            <Badge variant="secondary" className="ml-2 text-xs">
                              ₹{type.base_fare}/km
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors[`ambulance_${index}_type_id`] && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      {errors[`ambulance_${index}_type_id`]}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Current Latitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="19.0760"
                    value={ambulance.current_latitude}
                    onChange={(e) => updateAmbulance(index, 'current_latitude', e.target.value)}
                    className={`text-base ${errors[`ambulance_${index}_current_latitude`] ? 'border-red-500' : ''}`}
                  />
                  {errors[`ambulance_${index}_current_latitude`] && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      {errors[`ambulance_${index}_current_latitude`]}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    Current Longitude
                  </label>
                  <Input
                    type="number"
                    step="any"
                    placeholder="72.8777"
                    value={ambulance.current_longitude}
                    onChange={(e) => updateAmbulance(index, 'current_longitude', e.target.value)}
                    className={`text-base ${errors[`ambulance_${index}_current_longitude`] ? 'border-red-500' : ''}`}
                  />
                  {errors[`ambulance_${index}_current_longitude`] && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                      {errors[`ambulance_${index}_current_longitude`]}
                    </p>
                  )}
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium text-gray-700">
                    Initial Status
                  </label>
                  <Select 
                    value={ambulance.status} 
                    onValueChange={(value) => updateAmbulance(index, 'status', value)}
                  >
                    <SelectTrigger className="text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AVAILABLE">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm">Available</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="BUSY">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span className="text-sm">Busy</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="MAINTENANCE">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <span className="text-sm">Under Maintenance</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="OFFLINE">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                          <span className="text-sm">Offline</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || ambulances.length === 0}
          className="min-w-[140px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Registering...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Register {ambulances.length} Ambulance{ambulances.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>
      </div>
    </form>
  );

  if (inline) {
    return (
      <div className="w-full max-w-6xl">
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
          <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            Bulk Ambulance Registration
          </h2>
          <p className="text-gray-600">Register multiple ambulances at once. Choose between manual entry or CSV upload.</p>
        </div>

        <div className="space-y-6">
          {/* Registration Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Registration Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={registrationMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('manual')}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manual Entry</span>
                </Button>
                <Button
                  type="button"
                  variant={registrationMode === 'csv' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('csv')}
                  className="flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>CSV Upload</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {registrationMode === 'csv' && csvUploadSection}
          {registrationMode === 'manual' && manualEntrySection}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center space-x-2">
            <Truck className="w-5 h-5 text-blue-600" />
            <span>Bulk Register Ambulances</span>
          </DialogTitle>
          <DialogDescription className="pb-4 pl-7">
            Register multiple ambulances at once. Choose between manual entry or CSV upload.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Registration Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Registration Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={registrationMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('manual')}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Manual Entry</span>
                </Button>
                <Button
                  type="button"
                  variant={registrationMode === 'csv' ? 'default' : 'outline'}
                  onClick={() => setRegistrationMode('csv')}
                  className="flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>CSV Upload</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {registrationMode === 'csv' && csvUploadSection}
          {registrationMode === 'manual' && manualEntrySection}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAmbulanceRegistrationModal;